<template>
  <b-modal ref="modal" title="Edit Patient Information">
    <b-form @submit.prevent="submit">
      <validation-summary :errors="errors"></validation-summary>

      <v-form-group :validator="$v.updatedReport.patientName" label="Name">
        <b-form-input slot-scope="{ attrs, listeners }"
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="updatedReport.patientName"
                      placeholder="Patient Name" />
      </v-form-group>

      <v-form-group label="Date of Birth">
        <date-picker slot-scope="{ attrs, listeners }"
                     v-bind="attrs"
                     v-on="listeners"
                     v-model="updatedReport.patientBirthDate"
                     placeholder="Date of Birth" />
      </v-form-group>

      <v-form-group label="Address 1">
        <b-form-input slot-scope="{ attrs, listeners }"
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="updatedReport.survey.address.address1"
                      placeholder="Address 1" />
      </v-form-group>

      <v-form-group label="Address 2">
        <b-form-input slot-scope="{ attrs, listeners }"
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="updatedReport.survey.address.address2"
                      placeholder="Address 2" />
      </v-form-group>

      <v-form-group label="Zip">
        <b-form-input slot-scope="{ attrs, listeners }"
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="updatedReport.survey.address.zip"
                      placeholder="Zip" />
      </v-form-group>

      <v-form-group label="City">
        <b-form-input slot-scope="{ attrs, listeners }"
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="updatedReport.survey.address.city"
                      placeholder="City" />
      </v-form-group>

      <v-form-group label="State">
        <v-select slot-scope="{ attrs, listeners }"
                  v-bind="attrs"
                  v-on="listeners"
                  :options="usaStates.map(state => ({ label: state.name, value: state.name }))"
                  :clearable="false"
                  :filterable="false"
                  :searchable="false"
                  :reduce="option => option.value"
                  v-model="updatedReport.survey.address.state"
                  placeholder="State" />
      </v-form-group>

      <v-form-group label="Phone">
        <form-input-formatted slot-scope="{ attrs, listeners }"
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="updatedReport.patientPhone"
                      placeholder="Phone" />
      </v-form-group>

      <v-form-group :validator="$v.updatedReport.patientEmail" label="Email">
        <b-form-input slot-scope="{ attrs, listeners }"
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="updatedReport.patientEmail"
                      placeholder="Email" />
      </v-form-group>

      <v-form-group>
        <div class="edit-form-label">Gender</div>
        <b-form-radio v-model="updatedReport.patientGender" name="genders" value="MALE">
          <svg-icon name="male" /> Male
        </b-form-radio>
        <b-form-radio v-model="updatedReport.patientGender" name="genders" value="FEMALE">
          <svg-icon name="female" /> Female
        </b-form-radio>
      </v-form-group>

      <v-form-group v-if="report.status === ReportStatus.WaitingSurvey ||
                          report.status === ReportStatus.WaitingReview ||
                          report.status === ReportStatus.LabsWaitingSurvey"
                    label="Labwork Type"
                    :validator="$v.updatedReport.labworkType">
        <v-select slot-scope="{ attrs, listeners }"
                  v-bind="attrs"
                  v-on="listeners"
                  :options="labworkTypeOptions"
                  :class="[{ 'vs--has-value': updatedReport.labworkType }]"
                  :clearable="false"
                  :filterable="false"
                  :searchable="false"
                  :reduce="option => option.value"
                  v-model="updatedReport.labworkType"
                  placeholder="State" />
      </v-form-group>
    </b-form>
    <div slot="modal-footer">
      <b-btn @click="cancel" variant="outline-secondary">Cancel</b-btn>
      <b-btn :loading="loading" variant="success" @click="submit">Update</b-btn>
    </div>
  </b-modal>
</template>

<script>
  import modalFormMixin from "mixins/modal-form-mixin";
  import { required, email } from "vuelidate/lib/validators";

  import { createNamespacedHelpers } from 'vuex';
  const { mapState, mapActions } = createNamespacedHelpers('report');

  import { usaStates } from 'typed-usa-states';
  import ReportStatus from "enums/report-status";
  import LabworkType from "enums/labwork-type";

  import _ from "lodash";

export default {
  mixins: [modalFormMixin],

  data() {
    return {
      ReportStatus,

      updatedReport: {}
    };
  },

  validations: {
    updatedReport: {
      patientName: {
        required
      },

      patientEmail: {
        required,
        email
      },

      labworkType: {
        required
      }
    },
  },

  computed: {
    ...mapState(["report"]),

    usaStates() { return usaStates; },
    labworkTypeOptions() {
      return [
        { label: "Internal", value: LabworkType.Internal },
        { label: "By Client", value: LabworkType.ByClient },
        { label: "Client Draw", value: LabworkType.ClientDraw },
        { label: "Quest PSC", value: LabworkType.QuestPSC },
        { label: "Client Draw (Q)", value: LabworkType.QuestClientDraw },
        { label: "By Client (Q)", value: LabworkType.QuestByClient },``
      ]
    }
  },

  methods: {
    ...mapActions(['getById', 'updateReport']),

    async beforeShow(id) {
      await this.getById(id);

      this.updatedReport = _.cloneDeep(this.report);

      this.updatedReport.patientPhone = this.updatedReport.patientPhone === null ? "" : this.updatedReport.patientPhone;

      this.createForm();
    },

    createForm() {
      if (this.updatedReport.survey.address === null) {
        this.updatedReport.survey.address = {
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip: ''
        }
      }
    },

    async onSubmit() {
      this.updatedReport.isForUpdate = true;
      await this.updateReport(this.updatedReport);
    }
  }
};
</script>